import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useConfirm } from './confirmContext';
import { tr } from '@/i18n/tr';

/**
 * What is currently at risk, not merely whether something is. The two cases need
 * different dialogs: edit mode has no draft to fall back on, while create mode
 * only ever risks its picked photos (its text lives in a localStorage draft).
 */
export type UnsavedKind = 'none' | 'edits' | 'photos';

interface UnsavedValue {
  kind: UnsavedKind;
  setKind: (kind: UnsavedKind) => void;
}

const UnsavedContext = createContext<UnsavedValue | null>(null);

export function UnsavedProvider({ children }: { children: ReactNode }) {
  const [kind, setKind] = useState<UnsavedKind>('none');

  // Refresh / tab close. Registered once, driven by the same flag as the in-app
  // guard, so text-only create mode never triggers it — that text is drafted.
  useEffect(() => {
    if (kind === 'none') return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // required by Chrome to actually show the prompt
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [kind]);

  return <UnsavedContext.Provider value={{ kind, setKind }}>{children}</UnsavedContext.Provider>;
}

export function useUnsaved(): UnsavedValue {
  const ctx = useContext(UnsavedContext);
  if (!ctx) throw new Error('useUnsaved must be used inside <UnsavedProvider>');
  return ctx;
}

/**
 * Nav surfaces call this instead of navigating straight away:
 *   if (!(await guardExit())) return;
 * Returns true when leaving is safe (either nothing is at risk, or the user
 * confirmed the loss).
 */
export function useGuardedExit(): () => Promise<boolean> {
  const { kind } = useUnsaved();
  const confirm = useConfirm();
  return useCallback(async () => {
    if (kind === 'none') return true;
    return confirm(kind === 'photos' ? tr.confirm.discardPhotos : tr.confirm.discardEdits);
  }, [kind, confirm]);
}
